/** Content Operations Center — API bindings. */
import { http } from "@/shared/api/http";
import type { GeneratedAsset } from "@/entities/ai-studio";

export type AssetStatus = "draft" | "needs_review" | "approved" | "scheduled" | "published" | "archived";

export interface OpsCounter {
  status: AssetStatus;
  label: string;
  tone: string;
  count: number;
}

export interface PackAwaiting {
  id: string;
  objective: string;
  campaign_name: string | null;
  campaign_name_resolved?: string | null;
  preset: string | null;
  total_assets: number;
  needs_review_count: number;
}

export interface OpsSummary {
  workspace_id: string;
  counters: OpsCounter[];
  campaigns_awaiting_review: PackAwaiting[];
  average_review_time: string;
}

export type CommentType = "internal" | "revision_note" | "approval_note" | "regeneration_note";

export interface HistoryEvent {
  id: string;
  workspace_id: string;
  permanent_asset_id: string;
  campaign_pack_id: string | null;
  actor_id: string | null;
  type: string;
  payload: Record<string, unknown>;
  correlation_id: string | null;
  created_at: string;
}

export interface BulkOutcome {
  asset_id: string;
  /** Patch 37.4.43 · ``already_at_status`` distinguishes idempotent
   *  no-op from real forward transition; ``asset_not_final_deliverable``
   *  is emitted when the target requires a rendered deliverable
   *  (APPROVED / SCHEDULED / PUBLISHED on a spec-typed head).
   *  Patch 37.4.44 · ``needs_recovery`` is emitted when the raw
   *  Mongo document cannot be reconstructed via the domain model. */
  status:
    | "ok"
    | "already_at_status"
    | "illegal_transition"
    | "not_found"
    | "not_implemented_until_m6"
    | "asset_not_final_deliverable"
    | "needs_recovery";
  detail: string | null;
}

/** Patch 37.4.43 · Per-outcome counters — the client MUST prefer
 *  these over grepping ``outcomes`` so toast copy is stable.
 *  Patch 37.4.44 · ``needs_recovery`` is a typed blocked outcome
 *  for malformed head rows; the bulk operation refuses to
 *  transition them until the row is recovered. */
export interface BulkCounts {
  sent: number;
  already_at_status: number;
  illegal_transition: number;
  blocked_spec: number;
  not_found: number;
  not_implemented_until_m6: number;
  needs_recovery?: number;
  error: number;
}

export interface BulkResult {
  batch_id: string;
  outcomes: BulkOutcome[];
  /** Patch 37.4.43 · Optional to keep pre-37.4.43 backends compatible.
   *  When absent, the client can derive from ``outcomes`` — helper
   *  ``deriveBulkCounts`` in ``ops/counts.ts``. */
  counts?: BulkCounts;
}

export const STATUS_META: Record<AssetStatus, { label: string; classes: string }> = {
  draft:        { label: "Draft",         classes: "bg-zinc-200 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200" },
  needs_review: { label: "Needs Review",  classes: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" },
  approved:     { label: "Approved",      classes: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200" },
  scheduled:    { label: "Scheduled",     classes: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200" },
  published:    { label: "Published",     classes: "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200" },
  archived:     { label: "Archived",      classes: "bg-zinc-100 text-zinc-500 dark:bg-zinc-900/60 dark:text-zinc-400" },
};

export const opsSummary = (workspaceId: string): Promise<OpsSummary> =>
  http(`/workspaces/${workspaceId}/ops/summary`);

export const transitionAsset = (workspaceId: string, assetId: string, to: AssetStatus): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/assets/${assetId}/transition`, { method: "POST", body: { to } });

export const getAssetHistory = (workspaceId: string, assetId: string): Promise<HistoryEvent[]> =>
  http(`/workspaces/${workspaceId}/assets/${assetId}/history`);

export const addComment = (workspaceId: string, assetId: string, payload: { type: CommentType; body: string; target_version_number?: number }): Promise<HistoryEvent> =>
  http(`/workspaces/${workspaceId}/assets/${assetId}/comments`, { method: "POST", body: payload });

export const listVersions = (workspaceId: string, permanentAssetId: string): Promise<GeneratedAsset[]> =>
  http(`/workspaces/${workspaceId}/assets/by-permanent-id/${permanentAssetId}/versions`);

export const restoreVersion = (workspaceId: string, assetId: string): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/assets/${assetId}/restore`, { method: "POST" });

export const bulkApprove = (workspaceId: string, asset_ids: string[]): Promise<BulkResult> =>
  http(`/workspaces/${workspaceId}/assets/bulk/approve`, { method: "POST", body: { asset_ids } });

export const bulkReturn = (workspaceId: string, asset_ids: string[], note?: string): Promise<BulkResult> =>
  http(`/workspaces/${workspaceId}/assets/bulk/return`, { method: "POST", body: { asset_ids, note } });

export const bulkArchive = (workspaceId: string, asset_ids: string[]): Promise<BulkResult> =>
  http(`/workspaces/${workspaceId}/assets/bulk/archive`, { method: "POST", body: { asset_ids } });

export const approvePack = (workspaceId: string, packId: string): Promise<BulkResult> =>
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}/approve-all`, { method: "POST" });

export const returnPack = (workspaceId: string, packId: string, note?: string): Promise<BulkResult> =>
  // Patch 37.4.43 · The pack-scoped return-all endpoint derives its
  // id list server-side via ``_pack_asset_ids`` and ignores any
  // client-supplied ``asset_ids`` (``PackScopedBulkIn`` has extra=
  // "ignore" by default). Sending just the note keeps the wire
  // minimal and the intent honest.
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}/return-all`, { method: "POST", body: { note } });

export const archivePack = (workspaceId: string, packId: string): Promise<BulkResult> =>
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}/archive-all`, { method: "POST" });
